import { Component, OnDestroy, OnInit } from '@angular/core';
import { NbMenuService, NbSidebarService } from '@nebular/theme';
import { UserApiService, AuthModules } from '@api/user';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Event, Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { FeatureMenuItem, MenuItemType } from './feature.menu.item';
import { AsMenuService, enmMenuType, Menudata } from '../shared/helpers/as-menu.service';
import { FeaturesCache } from './features.cache';
import { PatientApiService } from '../../api/patient';
import Helpers, { ReportMenuNames } from '../helpers';
import { EvaluationApiService, EvaluationShowType } from '@api/evaluation';
import { ReportApiService } from '@api/reports';
import { environment } from '@environment/environment';

export enum enmMenuItem {
  DatosPersonales = 'Datos Personales',
  WhatsAppMail = 'WhatsAppMail',
}

@Component({
  templateUrl: './features.component.html',
  styleUrls: ['./features.component.scss'],
})
export class FeaturesComponent implements OnDestroy, OnInit {
  private ngUnsubscribe: Subject<any> = new Subject();
  menu: Menudata;
  licenceId!: number;
  fromMedicalConsultation: boolean = false;
  private lastMenuUrl: string | null = null;
  showLegalTermsModal = false;
  isColegio = environment.brand === 'colegio';
  isAcceptingLegalTerms = false;
  showZoomRecommendationNotice = false;
  isZoomRecommendationNoticeClosing = false;
  private isEditingConsultation = false;
  private zoomRecommendationTimeout: number | null = null;
  private zoomRecommendationFadeTimeout: number | null = null;

  constructor(
    private menuSideBar: NbMenuService,
    private sidebarService: NbSidebarService,
    private authService: UserApiService,
    private router: Router,
    private menuService: AsMenuService,
    private featuresCache: FeaturesCache,
    private patientService: PatientApiService,
    private route: ActivatedRoute,
    private evaluationService: EvaluationApiService,
    private reportService: ReportApiService,
  ) {
    this.route.queryParams.subscribe((params) => {
      this.fromMedicalConsultation = params['fromMedicalConsultation'] === 'true';
    });
    this.menuSideBar
      .onItemClick()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((item) => {
        const btn = item.item;
        if (btn.link) {
          this.setMenu(btn.link);
        }
        if (btn.title == 'Salir') this.authService.logOut();
      });
    this.router.events.subscribe((e: Event) => this.refreshMenu(e));

    this.menuService.onResetPatientDataMenu.subscribe(() => {
      if (this.featuresCache.patientSnapshot?.id) {
        this.patientService.get(this.featuresCache.patientSnapshot.id.toString()).subscribe((data) => {
          this.featuresCache.patientSnapshot = data;
          let findedIndexDP = this.menu.items.findIndex((d) => d.title == enmMenuItem.DatosPersonales);
          if (findedIndexDP >= 0) this.menu.items.splice(findedIndexDP, 1, this.getDatosPersonalesMenuItem());
          let findedIndexWP = this.menu.items.findIndex((d) => d.title == enmMenuItem.WhatsAppMail);
          if (findedIndexWP >= 0) this.menu.items.splice(findedIndexWP, 1, this.getWhatsappMailMenuItem());
          this.menuService.setMenu(this.menu);
        });
      }
    });
  }

  ngOnInit(): void {
    this.showLegalTermsModal = this.isColegio && this.authService.getLoggedUser()?.userDetails?.tc === false;
    this.showZoomRecommendationNotice = this.authService.shouldShowZoomRecommendationNotice();

    if (this.showZoomRecommendationNotice) {
      this.zoomRecommendationTimeout = window.setTimeout(() => this.closeZoomRecommendationNotice(), 8000);
    }
  }

  ngOnDestroy(): void {
    this.clearZoomRecommendationNoticeTimers();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  closeZoomRecommendationNotice(): void {
    if (!this.showZoomRecommendationNotice || this.isZoomRecommendationNoticeClosing) {
      return;
    }

    this.isZoomRecommendationNoticeClosing = true;
    this.clearZoomRecommendationNoticeTimers();
    this.zoomRecommendationFadeTimeout = window.setTimeout(() => {
      this.showZoomRecommendationNotice = false;
      this.isZoomRecommendationNoticeClosing = false;
      this.zoomRecommendationFadeTimeout = null;
    }, 250);
  }

  private clearZoomRecommendationNoticeTimers(): void {
    if (this.zoomRecommendationTimeout !== null) {
      window.clearTimeout(this.zoomRecommendationTimeout);
      this.zoomRecommendationTimeout = null;
    }

    if (this.zoomRecommendationFadeTimeout !== null) {
      window.clearTimeout(this.zoomRecommendationFadeTimeout);
      this.zoomRecommendationFadeTimeout = null;
    }
  }

  acceptLegalTerms(): void {
    if (this.isAcceptingLegalTerms) {
      return;
    }

    this.isAcceptingLegalTerms = true;
    this.authService.acceptLegalTerms().subscribe({
      next: () => {
        this.showLegalTermsModal = false;
        this.isAcceptingLegalTerms = false;
      },
      error: () => {
        this.isAcceptingLegalTerms = false;
        this.cancelLegalTerms();
      },
    });
  }

  cancelLegalTerms(): void {
    this.authService.logOut();
    this.router.navigate(['/']);
  }

  refreshMenu(event: Event) {
    if (event instanceof NavigationEnd) {
      let { url } = event;
      let dashboardUrl = '/admin/patient/dashboard/';
      if (url.startsWith(dashboardUrl)) {
        let user = this.authService.getUser();
        let userUrl = dashboardUrl + user.id_person;
        if (user.roles == 'ROLE_PATIENT' && url != userUrl) {
          this.router.navigate([userUrl]);
          return;
        }
      }
      this.setMenu(url);
    }
  }

  setMenu(url: string): void {
    if (this.lastMenuUrl === url && this.menu) {
      return;
    }

    const reportPatientId = this.getReportPatientId(url);
    if (reportPatientId && this.featuresCache.patientSnapshot?.id !== reportPatientId) {
      this.patientService.get(reportPatientId.toString()).subscribe((patient) => {
        this.featuresCache.patientSnapshot = patient;
        this.lastMenuUrl = null;
        this.setMenu(url);
      });
      return;
    }

    this.lastMenuUrl = url;
    this.isEditingConsultation = /\/admin\/patient\/\d+\/consultation\/[^/]+\/\d+\/\d+\/edit(?:\?|$)/.test(url);

    let user = this.authService.getUser();
    if (user.roles == 'ROLE_PATIENT') {
      if (url.includes('admin/perfil')) {
        this.menu = this.getProfileMenu();
      } else {
        this.menu = this.getMenuRolPatient();
      }

      this.menuService.setMenu(this.menu);
      return;
    }

    let type: enmMenuType;
    let forceUpdate: boolean = this.isEditingConsultation;
    if (
      url.includes('admin/patient/consultation') ||
      url.includes('admin/patient/list-consultation') ||
      url.includes('admin/patient/exams/new') ||
      url.includes('admin/patient/plans/new') ||
      url.includes('admin/patient/reports') ||
      url.includes('admin/patient/detail') ||
      url.includes('admin/patient/detail-2c') ||
      url.includes('admin/patient/patient-objectives') ||
      url.includes('admin/patient/plans/') ||
      url.includes('admin/patient/exams/')
    ) {
      type = enmMenuType.PatientConsultation;
    } else if (
      url == '/admin/patient' ||
      url.includes('admin/patient/new') ||
      url.includes('admin/patient/edit') ||
      url.includes('admin/professional') ||
      url.includes('admin/configuration') ||
      url.includes('admin/report/multiple') ||
      url.includes('admin/report?reportType=patient-multiple') ||
      url.includes('admin/report/dinamica') ||
      url.includes('admin/report/list-consultation') ||
      url.includes('admin/central-turnero')
    )
      type = enmMenuType.Configuration;
    else if (url.includes('admin/perfil')) type = enmMenuType.Profile;
    else type = enmMenuType.Patient;

    // Forzar actualización para rutas que requieren validación de tipo de evaluación
    const requiresEvaluationValidation =
      url.includes('admin/patient/list-consultation') || url.includes('admin/patient/detail');

    // El menú de Configuración (Herramientas/Pacientes/etc.) depende de varias comprobaciones
    // de permisos independientes entre sí; cachearlo por "type" hace que, si la primera vez que
    // se calculó faltó algún permiso, quede "congelado" incompleto hasta que algo más lo recalcule
    // (p. ej. entrar a una subsección de Herramientas). Al ser síncrono y sin llamadas HTTP, se
    // recalcula siempre para evitar ese menú desactualizado.
    const isConfigurationMenu = type === enmMenuType.Configuration;

    if (!forceUpdate && !requiresEvaluationValidation && !isConfigurationMenu && this.menu && this.menu.type === type) return;

    if (type === enmMenuType.PatientConsultation) {
      // Validación especial para list-consultation y detail
      if (url.includes('admin/patient/list-consultation') || url.includes('admin/patient/detail')) {
        const patientId = this.featuresCache.patientSnapshot?.id;
        this.menu = this.getPatientConsultationMenu();
        this.menuService.setMenu(this.menu);
        if (patientId) {
          this.evaluationService.getShowTypesEvaluations(patientId).subscribe((evaluationType: EvaluationShowType) => {
            let newMenu = this.getPatientConsultationMenuWithEvaluationType(evaluationType);
            this.menu = newMenu;
            this.menuService.setMenu(this.menu);
          });
          return; // Salir aquí para evitar establecer el menú antes de la validación
        }
      }

      let newMenu = this.getPatientConsultationMenu();

      if (this.menu && this.menu.type == type) {
        // Filtra los reportes si no existen en el menú anterior
        if (!this.menu.items.some((m) => m.title == ReportMenuNames.REPORTES_2C)) {
          newMenu.items = newMenu.items.filter((o) => o.title !== ReportMenuNames.REPORTES_2C);
        }
        if (!this.menu.items.some((m) => m.title == ReportMenuNames.REPORTES_5C)) {
          newMenu.items = newMenu.items.filter((o) => o.title !== ReportMenuNames.REPORTES_5C);
        }
      }
      if (type === enmMenuType.PatientConsultation || url.includes('admin/patient/detail/')) {
        let newMenu = this.getPatientConsultationMenu();

        if (this.menu && this.menu.type == type) {
          // Filtra los reportes si no existen en el menú anterior
          if (!this.menu.items.some((m) => m.title == ReportMenuNames.REPORTES_2C)) {
            newMenu.items = newMenu.items.filter((o) => o.title !== ReportMenuNames.REPORTES_2C);
          }
          if (!this.menu.items.some((m) => m.title == ReportMenuNames.REPORTES_5C)) {
            newMenu.items = newMenu.items.filter((o) => o.title !== ReportMenuNames.REPORTES_5C);
          }
        }
        this.menu = newMenu;
      }

      this.menu = newMenu;
    } else if (type === enmMenuType.Configuration) this.menu = this.getConfigurationmenu();
    else if (type === enmMenuType.Profile) this.menu = this.getProfileMenu();
    else this.menu = this.getMenuItemsPatient();

    this.menuService.setMenu(this.menu);
  }

  private getReportPatientId(url: string): number | null {
    if (!url.includes('/admin/report')) {
      return null;
    }

    const patientId = Number(this.router.parseUrl(url).queryParams.patientId);
    return Number.isFinite(patientId) && patientId > 0 ? patientId : null;
  }

  getProfileMenu(): Menudata {
    const user = this.authService.getUser();
    const isPatient = user?.roles === 'ROLE_PATIENT';
    const patientId = this.getPatientIdForProfileMenu();

    if (!isPatient) {
      return this.getConfigurationmenu();
    }

    let menuItems = [];
    menuItems.push({
      title: 'Perfil',
      type: MenuItemType.GROUP,
      icon: 'people',
      class: 'pb-2',
    });
    menuItems.push({
      title: 'Mi Perfil',
      link: '/admin/perfil/mi-perfil',
      type: MenuItemType.DEFAULT,
      class: 'mb-2',
    });

    if (patientId) {
      menuItems.push({
        title: 'Volver',
        link: `/admin/patient/dashboard/${patientId}`,
        type: MenuItemType.DEFAULT,
        class: 'mb-2',
      });
    }

    return { items: menuItems, showExit: true, type: enmMenuType.Profile };
  }

  private getPatientIdForProfileMenu(): number | null {
    const user = this.authService.getUser();
    const fromSnapshot = this.featuresCache?.patientSnapshot?.id;
    const fromUser = user?.id_person;
    const id = Number(fromSnapshot ?? fromUser);
    return isFinite(id) && id > 0 ? id : null;
  }

  getConfigurationmenu(): Menudata {
    let menuItems = [];
    const user = this.authService.getUser();
    const isPatient = user?.roles === 'ROLE_PATIENT';

    if (!isPatient) {
      menuItems.push({
        title: 'Perfil',
        type: MenuItemType.GROUP,
        icon: 'people',
        class: 'pb-2',
      });
      menuItems.push({
        title: 'Mi Perfil',
        link: '/admin/perfil/mi-perfil',
        type: MenuItemType.DEFAULT,
        class: 'mb-2',
      });
      if (environment.brand === 'antrosport') {
        menuItems.push({
          title: 'Gestión de cobros',
          link: '/migration/admin/configuration/payments',
          type: MenuItemType.DEFAULT,
          class: 'mb-2',
        });
        menuItems.push({
          title: 'Renovar suscripción',
          link: '/admin/perfil/renovar-suscripcion',
          type: MenuItemType.DEFAULT,
          class: 'mb-2',
        });
      }
    }

    const toolItems = [];
    if (this.authService.canAccessToDynamicConfiguration()) {
      toolItems.push({
        title: 'Formularios Dinámicos',
        link: '/admin/configuration/history',
        type: MenuItemType.DEFAULT,
      });
    }

    if (environment.brand === 'antrosport' && this.authService.canAccessToModule(AuthModules.CONFIG_REFERENCIA)) {
      toolItems.push({
        title: 'Tablas de Referencia',
        link: '/admin/configuration/group',
        type: MenuItemType.DEFAULT,
      });
    }
    if (this.authService.canAccessToModule(AuthModules.CONFIG_SEGMENTOS)) {
      toolItems.push({
        title: 'Segmentos',
        link: '/admin/configuration/segment',
        type: MenuItemType.DEFAULT,
      });
    }
    if (this.authService.canAccessToModule(AuthModules.CONFIG_COMIDAS)) {
      toolItems.push({
        title: 'Comidas',
        link: '/admin/configuration/food',
        type: MenuItemType.DEFAULT,
      });
    }
    if (this.authService.canAccessToModule(AuthModules.BACKOFFICE_SUSCRIPTION)) {
      toolItems.push(
        {
          title: 'Suscripciones',
          link: environment.brand === 'antrosport'
            ? '/migration/admin/configuration/suscripcion'
            : '/admin/configuration/suscripcion',
          type: MenuItemType.DEFAULT,
        },
        {
          title: 'Formulario Suscripción',
          link: environment.brand === 'antrosport'
            ? '/migration/admin/configuration/formulario-alta'
            : '/admin/configuration/suscripcion-sin-metodo-pago',
          type: MenuItemType.DEFAULT,
        }
      );
    }
    if (environment.brand === 'antrosport' && this.authService.canAccessToModule(AuthModules.CONFIG_LOGOS)) {
      toolItems.push({
        title: 'Personalizar Logos',
        link: '/admin/configuration/institute',
        type: MenuItemType.DEFAULT,
      });
    }
    if (environment.brand === 'antrosport' && this.authService.canAccessToModule(AuthModules.CONFIG_STAFF)) {
      toolItems.push({
        title: 'Staff',
        link: '/admin/professional',
        type: MenuItemType.DEFAULT,
      });
    }

    if (toolItems.length > 0) {
      menuItems.push({
        title: 'Herramientas',
        type: MenuItemType.GROUP,
        icon: 'settings-2',
      });
      menuItems.push(...toolItems);
    }

    if (this.authService.canAccessToModule(AuthModules.PACIENTE)) {
      menuItems.push({
        title: 'Pacientes',
        type: MenuItemType.GROUP,
        icon: 'people',
        class: 'pt-3',
      });
      menuItems.push(
        {
          title: 'Pacientes',
          link: environment.brand === 'antrosport' ? '/migration/admin/patient' : '/admin/patient',
          type: MenuItemType.DEFAULT,
        },
        {
          title: 'Agregar Paciente',
          link: environment.brand === 'antrosport' ? '/migration/admin/patient/new' : '/admin/patient/new',
          type: MenuItemType.DEFAULT,
        }
      );
    }

    if (this.authService.canAccessToModule(AuthModules.REPORTE_GRUPAL)) {
      menuItems.push({
        title: 'Segmentos',
        type: MenuItemType.GROUP,
        icon: 'people',
        class: 'pt-3',
      });
      menuItems.push({
        title: 'Reporte grupal FA5C',
        link: '/admin/report/multiple',
        type: MenuItemType.DEFAULT,
      });
      if (this.authService.canAccessToModule(AuthModules.INFO_GRUPAL_DINAMICA)) {
        menuItems.push({
          title: 'Inf. grupal dinámica',
          link: '/admin/report/dinamica',
          type: MenuItemType.DEFAULT,
        });
      }
    }

    // Agregar Central de Turnos al menú de configuración
    if (this.authService.canAccessToModule(AuthModules.PACIENTE)) {
      menuItems.push({
        title: 'Central Turnos',
        type: MenuItemType.GROUP,
        icon: 'calendar-outline',
        class: 'pt-3',
      });
      menuItems.push(
        {
          title: 'Dias y horarios',
          link: '/admin/central-turnero/configurar-agenda',
          type: MenuItemType.DEFAULT,
        },
        {
          title: 'Link video',
          link: '/admin/central-turnero/link-videoconsulta',
          type: MenuItemType.DEFAULT,
        },
        {
          title: 'Tipo de consulta',
          link: environment.brand === 'antrosport'
            ? '/migration/admin/central-turnero/tipos-consulta'
            : '/admin/central-turnero/tipos-consulta',
          type: MenuItemType.DEFAULT,
        },
        {
          title: 'Calendario de Turnos',
          link: '/admin/central-turnero/calendario-turnos',
          type: MenuItemType.DEFAULT,
        }
      );
    }

    return { items: menuItems, showExit: true, type: enmMenuType.Configuration };
  }

  getLicenceAccount() {
    return this.reportService.getDataSuscriptionAccount();
  }

  getPatientConsultationMenu(): Menudata {
    let patientId = this.featuresCache.patientSnapshot?.id;
    let menu = this.getSharedUserMenus();
    this.getSharedPatientConsultationMenus().forEach((m) => menu.push(m));
    return { items: menu, showExit: false, type: enmMenuType.PatientConsultation };
  }

  getPatientConsultationMenuWithEvaluationType(evaluationType: EvaluationShowType): Menudata {
    let patientId = this.featuresCache.patientSnapshot?.id;
    let menu = this.getSharedUserMenus();
    this.getSharedPatientConsultationMenusWithEvaluationType(evaluationType).forEach((m) => menu.push(m));
    return { items: menu, showExit: false, type: enmMenuType.PatientConsultation };
  }

  private getUserNameMenuItem(): FeatureMenuItem {
    return {
      title: Helpers.getPatientFullName(this.featuresCache.patientSnapshot),
      group: true,
      type: MenuItemType.NAME,
    };
  }

  private getUserAgeMenuItem(): FeatureMenuItem {
    return {
      title: Helpers.getPatientAge(this.featuresCache.patientSnapshot),
      group: true,
      type: MenuItemType.AGE,
    };
  }

  private getWhatsappMailMenuItem(): FeatureMenuItem {
    return {
      title: enmMenuItem.WhatsAppMail.toString(),
      link: this.featuresCache.urlWpp(),
      data: this.featuresCache.urlMail(),
      type: MenuItemType.WHATSAPP_MAIL,
    };
  }

  private getDatosPersonalesMenuItem(): FeatureMenuItem {
    return {
      title: enmMenuItem.DatosPersonales.toString(),
      link: '/admin/configuration/history',
      data: this.featuresCache.patientSnapshot,
      type: MenuItemType.DROPDOWN,
      extra: [
        { key: 'E-Mail', value: this.featuresCache.patientSnapshot?.email },
        {
          key: 'Tel',
          value: this.featuresCache.telFullNumber(),
          data: { areaCode: this.featuresCache.telAreaCode(), number: this.featuresCache.telNumber() },
        },
        { key: 'Última consulta', value: this.featuresCache.lastEvaluationDate() },
        {
          key: 'Segmento',
          value: this.featuresCache.patientSnapshot?.personSegment?.description,
          id: this.featuresCache.patientSnapshot?.personSegment?.id,
        },
      ],
    };
  }

  toggleSidebar() {
    this.sidebarService.toggle();
  }

  isNutricionista(): boolean {
    return this.authService.getUser()?.roles === 'ROLE_NUTRITIONIST';
  }

  getMenuRolPatient(): Menudata {
    let patientId = this.featuresCache.patientSnapshot?.id;
    let patient = this.featuresCache.patientSnapshot;
    let menu = [];

    menu.push(
      {
        title: 'UserBadge',
        link: '/admin/configuration/history',
        data: patient,
        type: MenuItemType.IMAGE_HEADER,
      },
      // Aquí deberías llamar a los métodos que tengas para los otros items
      this.getUserNameMenuItem(),
      this.getUserAgeMenuItem(),
      this.getDatosPersonalesMenuItem(),
      this.getWhatsappMailMenuItem(),
    );
    menu.push({
      title: 'Panel de Control',
      link: `/admin/patient/dashboard/${patientId}`,
      type: MenuItemType.DEFAULT,
      icon: 'settings-2',
    });
    if (this.authService.canAccessToModule(AuthModules.PACIENTE_PLANES)) {
      menu.push({
        title: 'Planes de Alimentación',
        link: `/admin/patient/plans/${patientId}`,
        type: MenuItemType.DEFAULT,
      });
    }
    const user = this.authService.getUser();

    if (user?.roles !== 'ROLE_PATIENT') {
      menu.push({
        title: 'Archivos Adjuntos',
        link: `/admin/patient/exams/${patientId}`,
        type: MenuItemType.DEFAULT,
      });
    }
    return { items: menu, showExit: false, type: enmMenuType.Patient };
  }

  // Se encarga de mostrar los items de los reportes 2C y 5C en el menú de la consulta del paciente
  getSharedPatientConsultationMenus(): FeatureMenuItem[] {
    let patientId = this.featuresCache.patientSnapshot?.id;
    let menuItems = [];

    // Ocultar los reportes 2C y 5C para ROLE_PATIENT
    const user = this.authService.getUser();
    const isPatient = user && user.roles === 'ROLE_PATIENT';

    if (!isPatient) {
      menuItems.push({
        title: 'Reportes',
        type: MenuItemType.GROUP,
        icon: 'settings-2',
      });
      menuItems.push(
        {
          title: ReportMenuNames.REPORTES_2C,
          link: `/admin/patient/detail-2c/${patientId}`,
          type: MenuItemType.DEFAULT,
        },
        {
          title: ReportMenuNames.REPORTES_5C,
          link: `/admin/patient/detail/${patientId}`,
          type: MenuItemType.DEFAULT,
        },
      );
    }

    return menuItems;
  }

  // Se encarga de mostrar los items de los reportes 2C y 5C filtrados según el tipo de evaluación
  getSharedPatientConsultationMenusWithEvaluationType(evaluationType: EvaluationShowType): FeatureMenuItem[] {
    let patientId = this.featuresCache.patientSnapshot?.id;
    let menuItems = [];

    // Ocultar los reportes 2C y 5C para ROLE_PATIENT
    const user = this.authService.getUser();
    const isPatient = user && user.roles === 'ROLE_PATIENT';

    if (!isPatient) {
      menuItems.push({
        title: 'Reportes',
        type: MenuItemType.GROUP,
        icon: 'settings-2',
      });

      // Agregar reportes según el tipo de evaluación
      if (evaluationType === EvaluationShowType.FA2C) {
        menuItems.push({
          title: ReportMenuNames.REPORTES_2C,
          link: `/admin/patient/detail-2c/${patientId}`,
          type: MenuItemType.DEFAULT,
        });
      } else if (evaluationType === EvaluationShowType.FA5C) {
        menuItems.push({
          title: ReportMenuNames.REPORTES_5C,
          link: `/admin/patient/detail/${patientId}`,
          type: MenuItemType.DEFAULT,
        });
      } else if (evaluationType === EvaluationShowType.MIX) {
        // Si es MIX, mostrar ambos reportes
        menuItems.push(
          {
            title: ReportMenuNames.REPORTES_2C,
            link: `/admin/patient/detail-2c/${patientId}`,
            type: MenuItemType.DEFAULT,
          },
          {
            title: ReportMenuNames.REPORTES_5C,
            link: `/admin/patient/detail/${patientId}`,
            type: MenuItemType.DEFAULT,
          },
        );
      }
      // Si es NONE, no se agregan reportes (solo el grupo "Reportes" queda)
    }

    return menuItems;
  }

  // Se encarga el resto de items del menú de la consulta del paciente
  getPatientMenu(): Menudata {
    let patientId = this.featuresCache.patientSnapshot?.id;
    let menu = this.getSharedUserMenus();
    return { items: menu, showExit: false, type: enmMenuType.Patient };
  }

  getMenuItemsPatient(): Menudata {
    let patientId = this.featuresCache.patientSnapshot?.id;

    let patient = this.featuresCache.patientSnapshot;

    let menu = [];

    if (this.authService.canEditModule(AuthModules.PACIENTE_EVALUACIONES)) {
      menu.push(
        {
          title: 'UserBadge',
          link: '/admin/configuration/history',
          data: patient,
          type: MenuItemType.IMAGE_HEADER,
        },
        this.getUserNameMenuItem(),
        this.getUserAgeMenuItem(),
        this.getDatosPersonalesMenuItem(),
        this.getWhatsappMailMenuItem(),
      );
      menu.push({
        title: 'Panel de Control',
        link: `/admin/patient/dashboard/${patientId}`,
        type: MenuItemType.DEFAULT,
        icon: 'settings-2',
      });

      let addButtonTitle = 'Agregar Consulta';

      if (this.hasCurrentConsultation()) {
        addButtonTitle = 'Editar Consulta';
      }

      if (addButtonTitle == 'Agregar Consulta') {
        menu.push({
          title: 'Consultas',
          link: '',
          type: MenuItemType.GROUP,
          icon: 'settings-2',
        });
        if (!this.fromMedicalConsultation || this.isEditingConsultation) {
          menu.push({
            title: addButtonTitle,
            link: `/admin/patient/consultation/new/${patientId}`,
            type: MenuItemType.DEFAULT,
          });
        }
        if (!this.isNutricionista()) {
          menu.push({
            title: 'Historicas',
            link: `/admin/patient/list-consultation/${patientId}`,
            type: MenuItemType.DEFAULT,
          });
        }
      } else if (addButtonTitle == 'Editar Consulta') {
        menu.push({
          title: 'Consultas',
          type: MenuItemType.GROUP,
          icon: 'settings-2',
        });
        if (!this.fromMedicalConsultation || this.isEditingConsultation) {
          menu.push({
            title: addButtonTitle,
            link: `/admin/patient/consultation/new/${patientId}`,
            type: MenuItemType.DEFAULT,
          });
        }
        if (!this.isNutricionista()) {
          menu.push({
            title: 'Historicas',
            link: `/admin/patient/list-consultation/${patientId}`,
            type: MenuItemType.DEFAULT,
          });
        }
      }

      if (this.authService.canAccessToModule(AuthModules.PACIENTE_ARCHIVOS)) {
        menu.push({
          title: 'Datos del Paciente',
          link: '',
          type: MenuItemType.GROUP,
          icon: 'settings-2',
        });
        if (this.authService.canAccessToPatientHistory()) {
          menu.push({
            title: 'Ficha del Paciente',
            link: `/admin/patient/reports/${patientId}`,
            type: MenuItemType.DEFAULT,
          });
        }
        if (this.authService.canAccessToModule(AuthModules.PACIENTE_OBJETIVO)) {
          menu.push({
            title: 'Objetivos del Paciente',
            link: `/admin/patient/patient-objectives/${patientId}`,
            type: MenuItemType.DEFAULT,
          });
        }
        if (this.authService.canAccessToModule(AuthModules.PACIENTE_PLANES)) {
          menu.push({
            title: 'Planes de Alimentación',
            link: `/admin/patient/plans/${patientId}`,
            type: MenuItemType.DEFAULT,
          });
        }
        const user = this.authService.getUser();
        if (user?.roles !== 'ROLE_PATIENT') {
          menu.push({
            title: 'Archivos Adjuntos',
            link: `/admin/patient/exams/${patientId}`,
            type: MenuItemType.DEFAULT,
          });
        }
      }
    }
    this.getSharedPatientConsultationMenus().forEach((m) => menu.push(m));
    return { items: menu, showExit: false, type: enmMenuType.Patient };
  }

  getSharedUserMenus(): FeatureMenuItem[] {
    let patientId = this.featuresCache.patientSnapshot?.id;

    let patient = this.featuresCache.patientSnapshot;

    let menuDefault = [];

    let addButtonTitle = 'Agregar Consulta';

    if (this.hasCurrentConsultation()) {
      addButtonTitle = 'Editar Consulta';
    }

    let action = this.authService.canEditModule(AuthModules.PACIENTE_CONSULTA) ? 'Agregar' : 'Ver';

    if (this.authService.canEditModule(AuthModules.PACIENTE_EVALUACIONES)) {
      if (addButtonTitle == 'Agregar Consulta') {
        menuDefault.push(
          {
            title: 'UserBadge',
            link: '/admin/configuration/history',
            data: patient,
            type: MenuItemType.IMAGE_HEADER,
          },
          this.getUserNameMenuItem(),
          this.getUserAgeMenuItem(),
          this.getDatosPersonalesMenuItem(),
          this.getWhatsappMailMenuItem(),
          {
            title: 'Panel de Control',
            link: `/admin/patient/dashboard/${patient?.id}`,
            type: MenuItemType.DEFAULT,
            icon: 'settings-2',
          },
          {
            title: 'Consultas',
            link: '',
            type: MenuItemType.GROUP,
            icon: 'settings-2',
          },
        );
        if (!this.fromMedicalConsultation || this.isEditingConsultation) {
          menuDefault.push({
            title: addButtonTitle,
            link: `/admin/patient/consultation/new/${patientId}`,
            class: 'font-weight-bold',
            type: MenuItemType.DEFAULT,
          });
        }
        if (!this.isNutricionista()) {
          menuDefault.push({
            title: 'Historicas',
            link: `/admin/patient/list-consultation/${patientId}`,
            type: MenuItemType.DEFAULT,
          });
        }
      } else if (addButtonTitle == 'Editar Consulta') {
        menuDefault.push(
          {
            title: 'UserBadge',
            link: '/admin/configuration/history',
            data: patient,
            type: MenuItemType.IMAGE_HEADER,
          },
          this.getUserNameMenuItem(),
          this.getUserAgeMenuItem(),
          this.getDatosPersonalesMenuItem(),
          this.getWhatsappMailMenuItem(),
          {
            title: 'Panel de Control',
            link: `/admin/patient/dashboard/${patient?.id}`,
            type: MenuItemType.DEFAULT,
            icon: 'settings-2',
          },
          {
            title: 'Consultas',
            link: '',
            type: MenuItemType.GROUP,
            icon: 'settings-2',
          },
        );
        if (!this.fromMedicalConsultation || this.isEditingConsultation) {
          menuDefault.push({
            title: addButtonTitle,
            link: `/admin/patient/consultation/new/${patientId}`,
            class: 'font-weight-bold',
            type: MenuItemType.DEFAULT,
          });
        }
        if (!this.isNutricionista()) {
          menuDefault.push({
            title: 'Historicas',
            link: `/admin/patient/list-consultation/${patientId}`,
            type: MenuItemType.DEFAULT,
          });
        }
      }
    }

    if (this.authService.canAccessToModule(AuthModules.PACIENTE_ARCHIVOS)) {
      menuDefault.push({
        title: 'Paciente',
        type: MenuItemType.GROUP,
        icon: 'people',
        class: 'pt-3',
      });
      if (this.authService.canAccessToPatientHistory()) {
        menuDefault.push({
          title: 'Ficha del Paciente',
          link: `/admin/patient/reports/${patientId}`,
          type: MenuItemType.DEFAULT,
        });
      }
      if (this.authService.canAccessToModule(AuthModules.PACIENTE_OBJETIVO)) {
        menuDefault.push({
          title: 'Objetivos del Paciente',
          link: `/admin/patient/patient-objectives/${patientId}`,
          type: MenuItemType.DEFAULT,
        });
      }
      if (this.authService.canAccessToModule(AuthModules.PACIENTE_PLANES)) {
        menuDefault.push({
          title: 'Planes de Alimentación',
          link: `/admin/patient/plans/${patientId}`,
          type: MenuItemType.DEFAULT,
        });
      }
      const currentUser = this.authService.getUser();
      if (currentUser?.roles !== 'ROLE_PATIENT') {
        menuDefault.push({
          title: 'Archivos Adjuntos',
          link: `/admin/patient/exams/${patientId}`,
          type: MenuItemType.DEFAULT,
        });
      }
    }

    return menuDefault;
  }

  private hasCurrentConsultation(): boolean {
    return this.isEditingConsultation || !!this.featuresCache.medicalConsultation;
  }
}
